import React, { Component, ReactNode } from 'react';
import { View, Text, TouchableOpacity, Platform } from 'react-native';
import tw from 'twrnc';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Track error in analytics if available
    if (typeof window !== 'undefined' && (window as any).amplitude) {
      (window as any).amplitude.track('error_boundary_triggered', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack
      });
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <View style={tw`flex-1 justify-center items-center p-6 bg-[#070816]`}>
          <View style={tw`bg-[#101126] border border-[#D9A441]/60 p-6 rounded-3xl shadow-lg max-w-md w-full`}>
            <Text style={tw`text-xl font-bold text-[#FFE89A] mb-4 text-center`}>
              Something went wrong
            </Text>
            <Text style={tw`text-[#F7EED7]/80 mb-4 text-center`}>
              We're sorry, but something unexpected happened. Please try refreshing the page.
            </Text>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <View style={tw`bg-[#070816] border border-[#D9A441]/30 p-3 rounded-xl mb-4`}>
                <Text style={tw`text-xs text-[#F7EED7]/70 font-mono`}>
                  {this.state.error.message}
                </Text>
              </View>
            )}
            <TouchableOpacity
              style={tw`bg-[#F7C948] py-3 px-6 rounded-2xl`}
              onPress={() => {
                this.setState({ hasError: false, error: undefined });
                // Only reload on web platform
                if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location) {
                  window.location.reload();
                }
                // For React Native, just reset the error state
              }}
            >
              <Text style={tw`text-[#24133A] text-center font-bold`}>
                {Platform.OS === 'web' ? 'Refresh Page' : 'Try Again'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      );
    }

    return this.props.children;
  }
}
